import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import cytoscape from 'cytoscape';
import cola from 'cytoscape-cola';

// Register cola layout
cytoscape.use(cola);

const ContractClusterView = () => {
    const { contractAddress } = useParams();
    const navigate = useNavigate();
    const { user, loading: authLoading } = useAuth();

    const [contractData, setContractData] = useState(null);
    const [clusters, setClusters] = useState([]);
    const [methods, setMethods] = useState([]);
    const [selectedCluster, setSelectedCluster] = useState(null);
    const [clusterGraph, setClusterGraph] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadingGraph, setLoadingGraph] = useState(false);
    const [error, setError] = useState(null);
    const [selectedNode, setSelectedNode] = useState(null);

    const containerRef = useRef(null);
    const cyRef = useRef(null);

    // Graph API base URL
    const GRAPH_API_URL = process.env.REACT_APP_GRAPH_API_URL ||
        (window.location.hostname === 'localhost' && window.location.port === '5173' ? 'http://localhost:3001' : '/graph-api');

    const baseUrl = window.location.hostname === 'localhost'
        ? 'http://localhost:3000'
        : '/api';

    // Fetch contract data and clusters
    useEffect(() => {
        if (contractAddress && !authLoading) {
            fetchContractData();
        }
    }, [contractAddress, authLoading]);

    const fetchContractData = async () => {
        setLoading(true);
        setError(null);

        try {
            // Fetch contract-level sybil data including methods
            const [statsRes, clustersRes, methodsRes] = await Promise.all([
                fetch(`${baseUrl}/v1/sybil/contract/${encodeURIComponent(contractAddress)}/stats`, { credentials: 'include' }),
                fetch(`${baseUrl}/v1/sybil/contract/${encodeURIComponent(contractAddress)}/clusters`, { credentials: 'include' }),
                fetch(`${baseUrl}/v1/sybil/contract/${encodeURIComponent(contractAddress)}/methods`, { credentials: 'include' })
            ]);

            if (!statsRes.ok) {
                // Fall back to customer endpoint if admin endpoint fails
                const customerStatsRes = await fetch(`${baseUrl}/v1/customer/sybil/stats/${encodeURIComponent(contractAddress)}`, { credentials: 'include' });
                if (!customerStatsRes.ok) {
                    throw new Error('Failed to fetch contract data');
                }
                const statsData = await customerStatsRes.json();
                setContractData(statsData);
            } else {
                const statsData = await statsRes.json();
                setContractData(statsData);
            }

            if (clustersRes.ok) {
                const clustersData = await clustersRes.json();
                const clusterList = (clustersData.clusters || []).map(c => ({
                    id: c.cluster_id,
                    walletCount: c.wallet_count,
                    totalClusterSize: c.total_cluster_size || c.wallet_count,
                    typologies: c.typology ? [c.typology] : (c.typologies || []),
                    firstSeen: c.first_seen,
                    lastSeen: c.last_seen,
                    callCount: c.call_count || 0
                }));
                setClusters(clusterList);

                // Auto-select first cluster
                if (clusterList.length > 0) {
                    setSelectedCluster(clusterList[0]);
                    fetchClusterGraph(clusterList[0]);
                }
            } else {
                // Try customer endpoint
                const customerClustersRes = await fetch(`${baseUrl}/v1/customer/sybil/clusters/${encodeURIComponent(contractAddress)}`, { credentials: 'include' });
                if (customerClustersRes.ok) {
                    const clustersData = await customerClustersRes.json();
                    const clusterList = (clustersData.clusters || []).map(c => ({
                        id: c.cluster_id,
                        walletCount: c.wallet_count,
                        totalClusterSize: c.total_cluster_size || c.wallet_count,
                        typologies: c.typology ? [c.typology] : [],
                        firstSeen: c.first_seen,
                        lastSeen: c.last_seen,
                        callCount: c.call_count || 0
                    }));
                    setClusters(clusterList);

                    if (clusterList.length > 0) {
                        setSelectedCluster(clusterList[0]);
                        fetchClusterGraph(clusterList[0]);
                    }
                }
            }

            if (methodsRes.ok) {
                const methodsData = await methodsRes.json();
                setMethods(methodsData.methods || []);
            }

        } catch (err) {
            console.error('Error fetching contract data:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchClusterGraph = async (cluster) => {
        if (!cluster || !cluster.id) return;

        setLoadingGraph(true);
        setClusterGraph(null);
        setSelectedNode(null);

        try {
            const response = await fetch(
                `${GRAPH_API_URL}/api/graph/full?community=${cluster.id}&chain=mainnet`,
                { credentials: 'include' }
            );

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            const data = await response.json();

            if (data.success && data.graph) {
                setClusterGraph({
                    nodes: data.graph.nodes || [],
                    edges: data.graph.edges || []
                });
            }
        } catch (err) {
            console.error('Error fetching cluster graph:', err);
        } finally {
            setLoadingGraph(false);
        }
    };

    const handleClusterSelect = (cluster) => {
        setSelectedCluster(cluster);
        fetchClusterGraph(cluster);
    };

    // Render cytoscape graph
    useEffect(() => {
        if (!clusterGraph || !containerRef.current) return;

        if (cyRef.current) {
            cyRef.current.destroy();
            cyRef.current = null;
        }

        const elements = [];

        (clusterGraph.nodes || []).forEach(node => {
            elements.push({
                data: {
                    id: node.id,
                    label: node.id.substring(0, 8) + '...',
                    type: node.type || 'wallet',
                    fullAddress: node.id,
                    isSybil: node.is_sybil || (node.status && node.status.includes('Sybil')),
                    community: node.community,
                    status: node.status || '',
                    typology: node.typology || ''
                }
            });
        });

        (clusterGraph.edges || []).forEach(edge => {
            elements.push({
                data: {
                    id: `${edge.source}-${edge.target}`,
                    source: edge.source,
                    target: edge.target
                }
            });
        });

        if (elements.length === 0) return;

        const cy = cytoscape({
            container: containerRef.current,
            elements,
            userZoomingEnabled: true,
            userPanningEnabled: true,
            minZoom: 0.2,
            maxZoom: 3,
            style: [
                {
                    selector: 'node',
                    style: {
                        'background-color': '#6c757d',
                        'label': 'data(label)',
                        'font-size': 8,
                        'color': '#fff',
                        'text-valign': 'bottom',
                        'text-margin-y': 5,
                        'width': 30,
                        'height': 30,
                        'border-width': 2,
                        'border-color': '#495057'
                    }
                },
                {
                    selector: 'node[isSybil = true]',
                    style: {
                        'background-color': '#dc3545',
                        'border-color': '#b02a37',
                        'border-width': 3
                    }
                },
                {
                    selector: 'edge',
                    style: {
                        'line-color': '#0d6efd',
                        'width': 1.5,
                        'target-arrow-shape': 'triangle',
                        'target-arrow-color': '#0d6efd',
                        'curve-style': 'bezier',
                        'arrow-scale': 0.8
                    }
                },
                {
                    selector: 'node:selected',
                    style: {
                        'border-width': 4,
                        'border-color': '#00ff00'
                    }
                }
            ],
            layout: {
                name: 'cola',
                animate: true,
                fit: true,
                padding: 30
            }
        });

        cy.on('tap', 'node', function (evt) {
            const node = evt.target;
            const data = node.data();
            setSelectedNode({
                address: data.fullAddress,
                isSybil: data.isSybil,
                status: data.status,
                typology: data.typology,
                connections: node.degree()
            });
        });

        cy.on('tap', function (evt) {
            if (evt.target === cy) {
                setSelectedNode(null);
            }
        });

        cy.ready(() => {
            cy.fit(undefined, 30);
        });

        cyRef.current = cy;

        return () => {
            if (cyRef.current) {
                cyRef.current.destroy();
                cyRef.current = null;
            }
        };
    }, [clusterGraph]);

    const formatNumber = (num) => {
        return (num || 0).toLocaleString();
    };

    const formatAddress = (addr) => {
        if (!addr || addr.length < 10) return addr;
        return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
    };

    if (authLoading || loading) {
        return (
            <div className="bg-dark text-light d-flex align-items-center justify-content-center" style={{ minHeight: '100vh' }}>
                <div className="text-center">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3 text-secondary">Loading contract data...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-dark text-light" style={{ minHeight: '100vh' }}>
                <div className="container-fluid py-4 px-4">
                    <button
                        className="btn btn-outline-secondary mb-4"
                        onClick={() => navigate(-1)}
                    >
                        <i className="bi bi-arrow-left me-2"></i>
                        Back
                    </button>
                    <div className="alert alert-danger">
                        <i className="bi bi-exclamation-triangle me-2"></i>
                        {error}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-dark text-light" style={{ minHeight: '100vh' }}>
            <div className="container-fluid py-4 px-4">
                {/* Header */}
                <div className="d-flex align-items-center justify-content-between mb-4">
                    <div className="d-flex align-items-center">
                        <button
                            className="btn btn-outline-secondary me-3"
                            onClick={() => navigate(-1)}
                        >
                            <i className="bi bi-arrow-left"></i>
                        </button>
                        <div>
                            <h4 className="mb-0 text-white">
                                <i className="bi bi-shield-exclamation text-danger me-2"></i>
                                Contract Sybil Analysis
                            </h4>
                            <p className="text-secondary small mb-0">
                                Detailed sybil cluster analysis for contract
                            </p>
                        </div>
                    </div>
                    {user?.is_admin && (
                        <span className="badge bg-danger">ADMIN</span>
                    )}
                </div>

                {/* Contract Info Card */}
                <div className="card bg-secondary-subtle text-light mb-4">
                    <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <h6 className="text-secondary mb-2">Contract Address</h6>
                                <code className="text-warning fs-5">{contractAddress}</code>
                                <button
                                    className="btn btn-link btn-sm ms-2"
                                    onClick={() => navigator.clipboard.writeText(contractAddress)}
                                >
                                    <i className="bi bi-clipboard text-secondary"></i>
                                </button>
                            </div>
                            <a
                                href={`https://etherscan.io/address/${contractAddress}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-outline-info btn-sm"
                            >
                                <i className="bi bi-box-arrow-up-right me-1"></i>
                                View on Etherscan
                            </a>
                        </div>
                    </div>
                </div>

                {/* Key Metrics */}
                {contractData && (
                    <div className="row g-3 mb-4">
                        <div className="col-md-3">
                            <div className="card bg-secondary-subtle text-light h-100">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <h6 className="text-secondary mb-1">Sybil Wallets</h6>
                                            <h3 className="mb-0 text-danger">
                                                {formatNumber(contractData.total_wallets || contractData.total_sybil_wallets)}
                                            </h3>
                                        </div>
                                        <i className="bi bi-wallet2 text-danger" style={{ fontSize: '2rem' }}></i>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="card bg-secondary-subtle text-light h-100">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <h6 className="text-secondary mb-1">Sybil Clusters</h6>
                                            <h3 className="mb-0 text-warning">{formatNumber(contractData.total_clusters)}</h3>
                                        </div>
                                        <i className="bi bi-diagram-3 text-warning" style={{ fontSize: '2rem' }}></i>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="card bg-secondary-subtle text-light h-100">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <h6 className="text-secondary mb-1">Total Interactions</h6>
                                            <h3 className="mb-0 text-info">
                                                {formatNumber(contractData.total_calls || contractData.total_interactions)}
                                            </h3>
                                        </div>
                                        <i className="bi bi-arrow-left-right text-info" style={{ fontSize: '2rem' }}></i>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="card bg-secondary-subtle text-light h-100">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <h6 className="text-secondary mb-1">Activity Period</h6>
                                            <p className="mb-0 small text-light">
                                                {contractData.first_seen ? new Date(contractData.first_seen).toLocaleDateString() : '-'}
                                                {' → '}
                                                {contractData.last_seen ? new Date(contractData.last_seen).toLocaleDateString() : '-'}
                                            </p>
                                        </div>
                                        <i className="bi bi-calendar-range text-success" style={{ fontSize: '2rem' }}></i>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Detected Typologies */}
                {contractData?.typologies && contractData.typologies.length > 0 && (
                    <div className="card bg-secondary-subtle text-light mb-4">
                        <div className="card-body">
                            <h5 className="card-title mb-3">
                                <i className="bi bi-tags text-warning me-2"></i>
                                Detected Attack Typologies
                            </h5>
                            <div className="d-flex flex-wrap gap-2">
                                {contractData.typologies.map((typ, idx) => (
                                    <span key={idx} className="badge bg-danger fs-6 px-3 py-2">
                                        {typ}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Methods Called by Sybils */}
                {methods.length > 0 && (
                    <div className="card bg-secondary-subtle text-light mb-4">
                        <div className="card-body">
                            <h5 className="card-title mb-3">
                                <i className="bi bi-code-slash text-info me-2"></i>
                                Methods Called by Sybils
                            </h5>
                            <p className="text-secondary small mb-3">
                                Contract functions most frequently called by detected sybil wallets
                            </p>
                            <div className="table-responsive">
                                <table className="table table-dark table-hover table-sm">
                                    <thead>
                                        <tr>
                                            <th>Method Signature</th>
                                            <th>Method Name</th>
                                            <th className="text-end">Total Calls</th>
                                            <th className="text-end">Unique Wallets</th>
                                            <th className="text-end">Unique Clusters</th>
                                            <th>Activity Range</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {methods.slice(0, 15).map((method, idx) => (
                                            <tr key={idx}>
                                                <td><code className="text-warning">{method.method_signature}</code></td>
                                                <td className="small">{method.method_name || '-'}</td>
                                                <td className="text-end">
                                                    <span className="badge bg-primary">{formatNumber(method.call_count)}</span>
                                                </td>
                                                <td className="text-end text-secondary">{formatNumber(method.unique_wallets)}</td>
                                                <td className="text-end text-secondary">{formatNumber(method.unique_clusters)}</td>
                                                <td className="small text-secondary">
                                                    {method.first_seen ? new Date(method.first_seen).toLocaleDateString() : '-'}
                                                    {' → '}
                                                    {method.last_seen ? new Date(method.last_seen).toLocaleDateString() : '-'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}

                {/* Cluster Analysis Section */}
                <div className="card bg-secondary-subtle text-light mb-4">
                    <div className="card-body">
                        <h5 className="card-title mb-3">
                            <i className="bi bi-diagram-3 text-success me-2"></i>
                            Sybil Cluster Analysis
                        </h5>
                        <p className="text-secondary small mb-3">
                            Select a cluster to view its network graph and detailed information
                        </p>

                        {clusters.length === 0 ? (
                            <div className="text-center py-5">
                                <i className="bi bi-shield-check text-success" style={{ fontSize: '4rem' }}></i>
                                <h4 className="text-white mt-3">No Sybil Clusters Detected</h4>
                                <p className="text-secondary">
                                    No sybil clusters have been detected interacting with this contract.
                                </p>
                            </div>
                        ) : (
                            <div className="row g-4">
                                {/* Cluster List */}
                                <div className="col-lg-4">
                                    <div className="bg-dark border border-secondary rounded p-3">
                                        <h6 className="text-white mb-3">
                                            <i className="bi bi-list-ul text-info me-2"></i>
                                            Clusters ({clusters.length})
                                        </h6>
                                        <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                                            {clusters.map((cluster, idx) => (
                                                <div
                                                    key={cluster.id}
                                                    className={`p-3 mb-2 rounded ${selectedCluster?.id === cluster.id
                                                        ? 'bg-secondary-subtle border border-primary border-2 shadow-sm'
                                                        : 'bg-secondary-subtle border border-secondary'
                                                        }`}
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => handleClusterSelect(cluster)}
                                                >
                                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                                        <span className="text-white fw-bold">Cluster #{cluster.id}</span>
                                                        <span className="badge bg-danger">
                                                            {cluster.walletCount} wallets
                                                        </span>
                                                    </div>
                                                    <div className="small text-secondary">
                                                        <div className="mb-1">
                                                            <i className="bi bi-people me-1"></i>
                                                            {cluster.walletCount} nodes called this contract
                                                        </div>
                                                        {cluster.totalClusterSize > cluster.walletCount && (
                                                            <div className="mb-1">
                                                                <i className="bi bi-diagram-3 me-1"></i>
                                                                Total cluster size: {cluster.totalClusterSize}
                                                            </div>
                                                        )}
                                                        {cluster.callCount > 0 && (
                                                            <div className="mb-1">
                                                                <i className="bi bi-arrow-repeat me-1"></i>
                                                                {formatNumber(cluster.callCount)} total calls
                                                            </div>
                                                        )}
                                                        <div className="mb-1">
                                                            <i className="bi bi-calendar me-1"></i>
                                                            {cluster.firstSeen ? new Date(cluster.firstSeen).toLocaleDateString() : '?'}
                                                            {' - '}
                                                            {cluster.lastSeen ? new Date(cluster.lastSeen).toLocaleDateString() : '?'}
                                                        </div>
                                                        {cluster.typologies && cluster.typologies.length > 0 && (
                                                            <div className="mt-1">
                                                                {cluster.typologies.slice(0, 2).map((t, i) => (
                                                                    <span key={i} className="badge bg-warning text-dark me-1" style={{ fontSize: '0.65rem' }}>
                                                                        {t}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Legend */}
                                    <div className="bg-dark border border-secondary rounded p-3 mt-3">
                                        <h6 className="text-secondary mb-3">Legend</h6>
                                        <div className="small">
                                            <div className="d-flex align-items-center mb-2">
                                                <div style={{ width: 16, height: 16, borderRadius: '50%', backgroundColor: '#dc3545', marginRight: 10 }}></div>
                                                <span className="text-white">Sybil Wallet</span>
                                            </div>
                                            <div className="d-flex align-items-center mb-2">
                                                <div style={{ width: 16, height: 16, borderRadius: '50%', backgroundColor: '#6c757d', marginRight: 10 }}></div>
                                                <span className="text-white">Normal Wallet</span>
                                            </div>
                                            <div className="d-flex align-items-center">
                                                <div style={{ width: 16, height: 2, backgroundColor: '#0d6efd', marginRight: 10 }}></div>
                                                <span className="text-white">Transfer</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Graph View */}
                                <div className="col-lg-8">
                                    {/* Selected Node Info */}
                                    {selectedNode && (
                                        <div className="bg-dark border border-info rounded p-3 mb-3">
                                            <h6 className="text-info mb-2">
                                                <i className="bi bi-circle-fill me-2" style={{ color: selectedNode.isSybil ? '#dc3545' : '#6c757d' }}></i>
                                                Wallet Details
                                            </h6>
                                            <div className="small">
                                                <div className="mb-1">
                                                    <span className="text-secondary">Address:</span>
                                                    <code className="text-info ms-2" style={{ fontSize: '0.75rem' }}>{selectedNode.address}</code>
                                                    <button
                                                        className="btn btn-link btn-sm p-0 ms-2"
                                                        onClick={() => navigator.clipboard.writeText(selectedNode.address)}
                                                    >
                                                        <i className="bi bi-clipboard text-secondary"></i>
                                                    </button>
                                                </div>
                                                {selectedNode.isSybil && (
                                                    <div className="text-danger mb-1">
                                                        <i className="bi bi-exclamation-triangle me-1"></i>
                                                        Sybil Detected
                                                    </div>
                                                )}
                                                {selectedNode.typology && (
                                                    <div className="mb-1">
                                                        <span className="text-secondary">Typology:</span>
                                                        <span className="text-warning ms-2">{selectedNode.typology}</span>
                                                    </div>
                                                )}
                                                <div>
                                                    <span className="text-secondary">Connections:</span>
                                                    <span className="text-white ms-2">{selectedNode.connections}</span>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Graph Container */}
                                    <div className="bg-dark border border-secondary rounded" style={{ position: 'relative' }}>
                                        {!selectedCluster && (
                                            <div className="d-flex align-items-center justify-content-center" style={{ height: '500px' }}>
                                                <div className="text-center">
                                                    <i className="bi bi-arrow-left text-secondary" style={{ fontSize: '3rem' }}></i>
                                                    <h5 className="text-secondary mt-3">Select a Cluster</h5>
                                                    <p className="text-secondary">Choose a cluster from the list to view its graph</p>
                                                </div>
                                            </div>
                                        )}

                                        {loadingGraph && (
                                            <div className="d-flex align-items-center justify-content-center" style={{ height: '500px' }}>
                                                <div className="text-center">
                                                    <div className="spinner-border text-primary"></div>
                                                    <p className="text-white mt-3">Loading cluster graph...</p>
                                                </div>
                                            </div>
                                        )}

                                        <div
                                            ref={containerRef}
                                            style={{
                                                height: '500px',
                                                width: '100%',
                                                display: clusterGraph && !loadingGraph ? 'block' : 'none'
                                            }}
                                        />

                                        {selectedCluster && clusterGraph && !loadingGraph && (
                                            <div className="position-absolute bottom-0 start-0 m-2 small text-secondary">
                                                Cluster #{selectedCluster.id} • {clusterGraph.nodes?.length || 0} nodes in full cluster • {clusterGraph.edges?.length || 0} edges
                                                <span className="ms-2 text-info">({selectedCluster.walletCount} interacted with this contract)</span>
                                            </div>
                                        )}
                                    </div>

                                    <div className="mt-2 small text-secondary">
                                        <i className="bi bi-info-circle me-2"></i>
                                        Click on nodes to see details • Drag to pan • Scroll to zoom
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ContractClusterView;


